/* ---------------------------------------------------------------------------
 * NZ payroll-style electronic-payments TXT file builder.
 *
 * Produces the COMMA-DELIMITED New Zealand electronic-payments file that matches
 * the existing payroll TXT format used for ANZ bulk uploads. It is NOT a
 * fixed-width direct-credit file — every field is separated by a comma.
 *
 * Layout (3 record types):
 *
 *   Header  (type 1): 1,,,,,,YYYYMMDD,YYYYMMDD,
 *   Payment (type 2): 2,{account_digits},52,{cents},{account_name},,,,{particulars},{reference},,,{batch_id}
 *   Trailer (type 3): 3,{total_cents},{transaction_count},{hash_total}
 *
 * Example:
 *   1,,,,,,20260526,20260526,
 *   2,1539530028610101,52,60539,BRIAN PINCOTT,,,,Commission,THE GOLD & BLACK GRO,,,EP000089
 *   3,60539,1,18650
 *
 * Rules implemented:
 *   - Comma-delimited (never fixed-width).
 *   - Bank account numbers have hyphens / spaces removed (digits only).
 *   - Amounts are whole cents, no decimal point.
 *   - Transaction code is always 52.
 *   - {account_name} = driver.bank_account_name.
 *   - {particulars}  = driver.payout_reference_override where available.
 *   - {reference}    = payer reference (defaults to "THE GOLD & BLACK GRO").
 *   - {batch_id}     = configurable, e.g. EP000090.
 *   - Trailer total  = sum of all payment-row cents.
 *   - Trailer count  = number of payment rows.
 *   - Trailer hash   = NZ payroll hash total of the destination accounts.
 * ------------------------------------------------------------------------- */

export type BankPayment = {
  /** Payee / bank account name as held on the account (driver.bank_account_name). */
  accountName: string;
  /** NZ bank account number in any of: BB-bbbb-AAAAAAA-SS or digits only. */
  accountNumber: string;
  /** Net pay in dollars (will be converted to whole cents). */
  amount: number;
  /** Particulars — driver.payout_reference_override where available. */
  particulars?: string;
};

export type BankFileMeta = {
  /** Date the bank should process the payments (Date Paid) — used for both header dates. */
  dueDate: Date;
  /**
   * The reference shown on each payment row (field after particulars).
   * Defaults to "THE GOLD & BLACK GRO" unless made configurable.
   */
  payerReference?: string;
  /** Batch / instruction id stamped on every payment row, e.g. "EP000090". */
  batchId?: string;
};

/* ---------- field helpers ---------- */

/** Strip everything except digits from an NZ account string. */
const digitsOnly = (s: string) => (s || "").replace(/\D/g, "");

/**
 * Normalise an NZ bank account number to the digits-only string the bank
 * expects, with EVERY segment padded to its canonical width and the suffix
 * ALWAYS left-padded to 3 digits.
 *
 * NZ format: BB-BBBB-AAAAAAAA-SSS  (bank-branch-account-suffix)
 *   - bank   : 2 digits
 *   - branch : 4 digits
 *   - account: 8 digits (7-digit accounts are left-padded to 8)
 *   - suffix : 2 or 3 digits  -> must ALWAYS be exported as 3 digits
 *
 * The canonical exported value is therefore ALWAYS 17 digits (2+4+8+3).
 *
 * Examples (the bank rejects anything else with "Invalid suffix"):
 *   15-3953-00286101-01  -> 15395300286101001
 *   06-0746-00809535-00  -> 06074600809535000
 *   38-9006-00663682-001 -> 38900600663682001
 *
 * Suffix padding: 01 -> 001, 00 -> 000, 10 -> 010, 99 -> 099, 100 -> 100.
 */
export const normalizeNzAccount = (raw: string): string => {
  const value = (raw || "").trim();
  if (!value) return "";

  // Preferred path: the account is written with separators (hyphens/spaces).
  // Split on any non-digit run, then pad each fixed-width segment.
  const parts = value.split(/[^0-9]+/).filter(Boolean);
  if (parts.length === 4) {
    const [bank, branch, account, suffix] = parts;
    return (
      bank.padStart(2, "0") +
      branch.padStart(4, "0") +
      account.padStart(8, "0") +
      suffix.padStart(3, "0")
    );
  }

  // Fallback: no separators — split using known NZ field widths.
  // bank(2) + branch(4) = 6 fixed; whatever remains is account + suffix.
  // The canonical account width is 8, so:
  //   15 digits = 2+4+7+2  -> 7-digit account, 2-digit suffix
  //   16 digits = 2+4+8+2  -> 8-digit account, 2-digit suffix
  //   17 digits = 2+4+8+3  -> 8-digit account, 3-digit suffix
  const d = digitsOnly(value);

  if (d.length === 15) {
    // 7-digit account + 2-digit suffix.
    const bankBranch = d.slice(0, 6);
    const account = d.slice(6, 13); // 7 digits
    const suffix = d.slice(13); // 2 digits
    return bankBranch + account.padStart(8, "0") + suffix.padStart(3, "0");
  }

  if (d.length === 16) {
    // 8-digit account + 2-digit suffix (the common stored form).
    const body = d.slice(0, 14); // bank(2)+branch(4)+account(8)
    const suffix = d.slice(14); // 2 digits
    return body + suffix.padStart(3, "0");
  }

  if (d.length === 17) {
    // Already the full 8-digit account + 3-digit suffix — return unchanged.
    return d;
  }

  if (d.length === 13 || d.length === 14) {
    // Account given without a suffix at all (2+4+7 or 2+4+8). Default suffix 000.
    const body = d.length === 13 ? d.slice(0, 6) + d.slice(6).padStart(8, "0") : d;
    return body + "000";
  }

  // Unknown shape: pad the trailing suffix to 3 anyway so a too-short suffix
  // is corrected, and let the caller flag anything genuinely malformed.
  if (d.length > 3) {
    return d.slice(0, -3) + d.slice(-3).padStart(3, "0");
  }
  return d;
};


/** Dollars -> whole cents (integer). */
const toCents = (dollars: number) => Math.round(Math.abs(Number(dollars) || 0) * 100);

/** YYYYMMDD for the header records (local date). */
const fmtYmd = (d: Date) =>
  `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

/**
 * NZ electronic-payments "hash total" of the destination accounts.
 *
 * The bank's control-record hash is computed by summing ONLY the
 * BRANCH + ACCOUNT-NUMBER portion of every destination account. It must
 * EXCLUDE:
 *   - the bank number (first 2 digits)
 *   - the suffix       (last 3 digits)
 *   - the amount
 *   - the transaction code
 *
 * NZ account layout (already normalised here to 17 digits):
 *   BB | BBBB | AAAAAAAA | SSS   = bank(2) branch(4) account(8) suffix(3)
 *
 * Hash component per account = branch(4) + account(8)  =  digits[2 .. len-3].
 *
 * Examples:
 *   15-3953-00286101-001 -> 395300286101
 *   06-0746-00809535-000 -> 074600809535
 *   38-9006-00663682-001 -> 900600663682
 *
 * The summed value is kept to its least-significant 11 digits (no leading
 * zeros), matching what the bank expects in the trailer/control record.
 */
const computeHashTotal = (accounts: string[]): string => {
  let hash = 0n;

  for (const raw of accounts) {
    const d = digitsOnly(raw);


// Expected format:
// BB BBBB AAAAAAAA SSS
// Example:
// 15 3953 00286101 001

if (d.length < 17) continue;

const branch = d.slice(2, 6);

/**
 * ANZ Domestic Extended Hash Total
 *
 * Calculation:
 * - Ignore bank number
 * - Ignore suffix
 * - Use branch number
 * - Use LAST 7 digits of base account
 * - Sum all values
 * - If result exceeds 11 digits, keep the right-most 11 digits
 *
 * Verified against ANZ Direct Online Bulkload
 * May 2026
 */
const baseAccount = d.slice(6, 14); // 8 digit base account
const last7Digits = baseAccount.slice(-7);

const hashComponent = branch + last7Digits;

hash += BigInt(hashComponent);
}

const hashString = hash.toString();

// ANZ requires overflow to be truncated from the left
return hashString.length > 11
  ? hashString.slice(-11)
  : hashString;
};



/** Auto-increment a payroll batch id like "EP000089" -> "EP000090". */
export const nextBatchId = (prev?: string): string => {
  const m = (prev || "").match(/^([A-Za-z]*)(\d+)$/);
  if (!m) return "EP000090";
  const prefix = m[1] || "EP";
  const width = m[2].length;
  const next = (Number(m[2]) + 1).toString().padStart(width, "0");
  return `${prefix}${next}`;
};

/* ---------- the builder ---------- */

export type BuildResult = {
  text: string;
  totalCents: number;
  count: number;
  hashTotal: string;
  batchId: string;
  /** Drivers we couldn't build a line for (missing account number or zero amount). */
  invalid: string[];
};

const DEFAULT_PAYER_REFERENCE = "THE GOLD & BLACK GRO";
const DEFAULT_BATCH_ID = "EP000090";

export const buildPayrollTxtFile = (
  payments: BankPayment[],
  meta: BankFileMeta,
): BuildResult => {
  const reference = (meta.payerReference || DEFAULT_PAYER_REFERENCE).slice(0, 20);
  const batchId = meta.batchId || DEFAULT_BATCH_ID;
  const ymd = fmtYmd(meta.dueDate);

  const lines: string[] = [];
  const invalid: string[] = [];
  const validAccounts: string[] = [];

  /* ----- Header (type 1): 1,,,,,,YYYYMMDD,YYYYMMDD, ----- */
  lines.push(`1,,,,,,${ymd},${ymd},`);

  /* ----- Payment rows (type 2) ----- */
  let totalCents = 0;
  let count = 0;

  for (const p of payments) {
    // Normalise to digits-only WITH the suffix left-padded to 3 digits so the
    // bank does not reject the upload with "Invalid suffix".
    const acct = normalizeNzAccount(p.accountNumber);
    const cents = toCents(p.amount);
    if (!acct || cents <= 0) {
      invalid.push(p.accountName);
      continue;
    }


    totalCents += cents;
    count += 1;
    validAccounts.push(acct);

    // Names / particulars are trimmed of commas so the delimiter stays intact.
    // ANZ Direct Online rejects the whole bulkload if "Other Party
    // Particulars" exceeds 12 characters (confirmed via a real Bulkload
    // Error Report -- this was previously capped at 20, which is why driver
    // wage rows passed silently but longer values, like a full driver name,
    // got rejected).
    const accountName = (p.accountName || "").replace(/,/g, " ").trim();
    const particulars = (p.particulars || "").replace(/,/g, " ").trim().slice(0, 12);

    // 2,{account},52,{cents},{name},,,,{particulars},{reference},,,{batch_id}
    lines.push(
      `2,${acct},52,${cents},${accountName},,,,${particulars},${reference},,,${batchId}`,
    );
  }

/* ----- Trailer (type 3): 3,{total_cents},{count},{hash_total} ----- */
const hashTotal = computeHashTotal(validAccounts);

console.log("VALID ACCOUNTS", validAccounts);
console.log("HASH TOTAL", hashTotal);

lines.push(`3,${totalCents},${count},${hashTotal}`);

  return {
    text: lines.join("\r\n") + "\r\n",
    totalCents,
    count,
    hashTotal,
    batchId,
    invalid,
  };
};
